import {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

import AnimatedLoader from "@hl/base-components/lib/AnimatedLoader";
import { logError } from "@hl/shared-features/lib/services/logger";
import {
  Box,
  Group,
  Stack,
  Switch,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { useAlert } from "react-alert";

import {
  useGetAccountNotificationSettingsQuery,
  useUpdateAccountSettingsMutation,
} from "../../queries/profile.graphql.generated";

type NotificationSettingsForm = {
  essentialUpdates: {
    email: boolean;
    push: boolean;
  };
  highlightUpdates: {
    email: boolean;
    push: boolean;
  };
  offerUpdates: {
    email: boolean;
    push: boolean;
  };
  offersAboveFloor: {
    email: boolean;
    push: boolean;
  };
};

const initialValues: NotificationSettingsForm = {
  essentialUpdates: {
    email: true,
    push: true,
  },
  highlightUpdates: {
    email: true,
    push: true,
  },
  offerUpdates: {
    email: true,
    push: true,
  },
  offersAboveFloor: {
    email: true,
    push: true,
  },
};

export const NotificationSettings = ({ active }: { active?: boolean }) => {
  const alert = useAlert();
  const form = useForm<NotificationSettingsForm>({
    initialValues,
  });
  const isDirty = form.isDirty();
  const values = form.values;

  const [newValues, setNewValues] = useState<NotificationSettingsForm | null>(
    null
  );
  const [debouncedValues] = useDebouncedValue(newValues, 1000);

  const [updateAccountSettingsMutation] = useUpdateAccountSettingsMutation();
  const { loading } = useGetAccountNotificationSettingsQuery({
    skip: !active,
    onCompleted(data) {
      const currentNotificationSettings =
        data?.getAccountSettings.notificationSettings;
      if (currentNotificationSettings != null) {
        const newValues = {
          ...values,
        } as NotificationSettingsForm;

        if (currentNotificationSettings.essentialUpdates) {
          newValues.essentialUpdates = {
            email: currentNotificationSettings.essentialUpdates.email,
            push: currentNotificationSettings.essentialUpdates.push,
          };
        }
        if (currentNotificationSettings.highlightUpdates) {
          newValues.highlightUpdates = {
            email: currentNotificationSettings.highlightUpdates.email,
            push: currentNotificationSettings.highlightUpdates.push,
          };
        }
        if (currentNotificationSettings.offerUpdates) {
          newValues.offerUpdates = {
            email: currentNotificationSettings.offerUpdates.email,
            push: currentNotificationSettings.offerUpdates.push,
          };
        }
        if (currentNotificationSettings.offersAboveFloor) {
          newValues.offersAboveFloor = {
            email: currentNotificationSettings.offersAboveFloor.email,
            push: currentNotificationSettings.offersAboveFloor.push,
          };
        }

        form.setValues(newValues);
        form.resetDirty(newValues);
      }
    },
  });

  const updateAccountSettings = useCallback(
    async (values: NotificationSettingsForm) => {
      try {
        await updateAccountSettingsMutation({
          variables: {
            input: {
              notificationSettings: values,
            },
          },
          // refetchQueries: [GetAccountNotificationSettingsDocument],
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        alert.error("Error updating settings. Refresh and try again.");
        await logError({ error, message: "update account settings error" });
      }
    },
    [updateAccountSettingsMutation, alert, values]
  );

  useEffect(() => {
    if (isDirty) {
      form.resetDirty();
      setNewValues(values);
    }
  }, [form, isDirty, values]);

  useEffect(() => {
    if (debouncedValues != null) {
      updateAccountSettings(debouncedValues);
    }
  }, [debouncedValues]);

  if (!active) {
    return null;
  }

  return (
    <Box maw={640}>
      <Stack spacing={32}>
        <Group>
          <Title order={5}>Notification preferences</Title>
        </Group>

        {loading ? (
          <Box>
            <Group position="center" py={32}>
              <AnimatedLoader aria-label="loading" size={60} />
            </Group>
          </Box>
        ) : (
          <>
            <Stack>
              <Group pr={16} position="apart">
                <Text>Basic notifications</Text>
                <Group>
                  <Text size={"xs"} w={42}>
                    EMAIL
                  </Text>
                  <Text size={"xs"} w={42}>
                    PUSH
                  </Text>
                </Group>
              </Group>

              <SettingField
                name="Essential updates"
                description="Get basic updates like when your items sell or your listings expire"
              >
                <Switch
                  pt={8}
                  w={42}
                  {...form.getInputProps("essentialUpdates.email", {
                    type: "checkbox",
                  })}
                />
                <Switch
                  pt={8}
                  w={42}
                  {...form.getInputProps("essentialUpdates.push", {
                    type: "checkbox",
                  })}
                />
              </SettingField>

              <SettingField
                name="Highlight updates"
                description="Get the latest news from Highlight about launches, updates & more"
              >
                <Switch
                  pt={8}
                  w={42}
                  {...form.getInputProps("highlightUpdates.email", {
                    type: "checkbox",
                  })}
                />
                <Switch
                  pt={8}
                  w={42}
                  {...form.getInputProps("highlightUpdates.push", {
                    type: "checkbox",
                  })}
                />
              </SettingField>
            </Stack>

            <Stack>
              <Group pr={16} position="apart">
                <Text>Offer notifications</Text>
                <Group>
                  <Text size={"xs"} w={42}>
                    EMAIL
                  </Text>
                  <Text size={"xs"} w={42}>
                    PUSH
                  </Text>
                </Group>
              </Group>

              <SettingField
                name="Offer alerts"
                description="Get alerts about offers on your NFTs"
              >
                <Switch
                  pt={8}
                  w={42}
                  {...form.getInputProps("offerUpdates.email", {
                    type: "checkbox",
                  })}
                />
                <Switch
                  pt={8}
                  w={42}
                  {...form.getInputProps("offerUpdates.push", {
                    type: "checkbox",
                  })}
                />
              </SettingField>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
};

type SettingFieldProps = {
  name: ReactNode;
  description: ReactNode;
  disabled?: boolean;
};
const SettingField = (props: PropsWithChildren<SettingFieldProps>) => {
  const { name, description, disabled, children } = props;
  const theme = useMantineTheme();

  return (
    <Box
      sx={{
        borderRadius: 6,
        border: `1px solid ${theme.colors.divider[0]}`,
        padding: "12px 16px",
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <Group noWrap sx={{ pointerEvents: disabled ? "none" : "all" }}>
        <Stack spacing={0} sx={{ width: "100%", flexGrow: 1, flexBasis: 0 }}>
          <Text size={"sm"} fw={500}>
            {name}
          </Text>
          <Text size={"sm"} c={"dimmed"}>
            {description}
          </Text>
        </Stack>
        {children}
      </Group>
    </Box>
  );
};
